import type { ReactNode } from 'react';
import { useStore } from '@nanostores/react';
import { PermissionsProvider } from '@customerio/hydra-permissions/react';
import { accountPermissionsForRole, type RoleDefinition } from '@customerio/hydra-permissions/roles';
import type { PermissionsUser } from '@customerio/hydra-permissions/validation';
import type { AccountPermission } from '@customerio/hydra-permissions';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';
import { accountStore, userStore } from '@/services/auth/store';
import { masqReadOnlyStore } from '@/utils/masq';
import type { AugmentedUser } from '@/services/auth/types';
import { isStandardRole, useRoleDefinitions } from '@/features/roles';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useListWorkspaces } from '@/features/workspaces';
import { useLocation } from '@/services/router/compat';
import { getWorkspaceIdParam } from '@/services/router/workspace-id-param';
import { PermissionsResolvedContext } from './resolved';

type DerivedPermissionsProviderProps = { children: ReactNode };

/**
 * Feeds the (pure, presentational) `PermissionsProvider` from global,
 * above-Pluma sources — the account/user nanostores, `masqReadOnlyStore`,
 * `useRoleDefinitions` (store + react-query), and the current-workspace
 * resolution — rather than deriving them inside the authenticated layout.
 *
 * Mounted above `ConnectedPlumaProvider`, this makes `usePermissions()` resolve
 * inside Pluma managed modals (which render at the root Pluma provider),
 * instead of falling back to its deny-all default (FPS-3066).
 *
 * All inputs are null-tolerant: on public routes (no authenticated account),
 * `checkPermission` naturally denies everything, matching the previous
 * no-provider behavior.
 */
export function DerivedPermissionsProvider({ children }: DerivedPermissionsProviderProps) {
  const account = useStore(accountStore);
  const user = useStore(userStore);
  const masqReadOnly = useStore(masqReadOnlyStore);
  const roleDefinitions = useRoleDefinitions();
  const currentWorkspace = useCurrentWorkspace();
  const workspaces = useListWorkspaces();
  const location = useLocation();

  const permissionsUser: PermissionsUser | null =
    user == null
      ? null
      : {
          account_permissions: user.account_permissions,
          workspace_roles: buildWorkspaceRoles(user),
          masq_permissions: user.masq_permissions ?? UserMasqPermission.NoMasqAccess,
        };

  const roles = buildRoleDefinitions(roleDefinitions.data, user);

  // Roles are keyed by the environment id, which `MergedWorkspace.id` holds. No
  // role is keyed by a CDP id, so do not select the id by bundle.
  const workspaceId = currentWorkspace?.id ?? null;

  // A workspace URL has no workspace to check against until the list loads. A
  // failed list leaves it unknown too; only a loaded list that lacks it is a
  // real "none".
  const isWorkspaceResolved =
    getWorkspaceIdParam(location.pathname) == null || currentWorkspace != null || workspaces.isSuccess;
  const isResolved = account != null && user != null && roles != null && isWorkspaceResolved;

  return (
    <PermissionsResolvedContext.Provider value={isResolved}>
      <PermissionsProvider
        user={permissionsUser}
        account={account}
        roles={roles}
        workspaceId={workspaceId}
        masqReadOnly={masqReadOnly}
      >
        {children}
      </PermissionsProvider>
    </PermissionsResolvedContext.Provider>
  );
}

function buildWorkspaceRoles(user: AugmentedUser): Record<number, { id?: number; name: string }> | null {
  if (user.roles == null) {
    return null;
  }

  const workspaceRoles: Record<number, { id?: number; name: string }> = {};
  for (const userRole of user.roles) {
    workspaceRoles[userRole.workspace_id] = { id: userRole.role_id, name: userRole.role_name };
  }
  return workspaceRoles;
}

function buildRoleDefinitions(
  roleDefinitions: ReturnType<typeof useRoleDefinitions>['data'],
  user: AugmentedUser | null,
): Record<string, RoleDefinition> | null {
  // `checkPermission` reads null as "no role data" and an empty object as "this
  // account has no roles". Both deny, but only one of them is an answer.
  if (roleDefinitions == null) {
    return null;
  }

  const roles: Record<string, RoleDefinition> = {};
  for (const role of Object.values(roleDefinitions)) {
    // Custom roles don't carry account permissions, so we populate what we have from the user.
    const accountPermissions: Set<AccountPermission> = isStandardRole(role.name)
      ? accountPermissionsForRole(role.name)
      : new Set(user?.account_permissions ?? []);

    // Role names are unique.
    roles[role.name] = {
      ...role,
      accountPermissions,
    };
  }
  return roles;
}
